'use client';
import { MODAL_TEST_IDS } from '@susu/custom-made/utils/testIds';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { ButtonType } from '../button/button';
import Button from '../button/button';

interface ModalProps {
  isOpen: boolean;
  hasOverlay?: boolean;
  title?: ReactNode;
  price?: string;
  description?: ReactNode;
  firstButtonText: ReactNode;
  firstButtonType: ButtonType;
  secondButtonText?: string;
  secondButtonType?: ButtonType;
  isRectangular?: boolean;
  isButtonContentSize?: boolean;
  isFullSize?: boolean;
  testId?: string;
  onFirstButtonClick?: () => void;
  onSecondButtonClick?: () => void;
  onOverlayClick?: () => void;
}

export default function Modal({
  isOpen,
  hasOverlay = true,
  title,
  price,
  description,
  firstButtonText,
  firstButtonType,
  secondButtonText,
  secondButtonType,
  isRectangular = true,
  isButtonContentSize,
  isFullSize,
  testId,
  onFirstButtonClick,
  onSecondButtonClick,
  onOverlayClick,
}: Readonly<ModalProps>) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(isOpen);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const modalElement = dialogRef.current;
    if (!modalElement) {
      return;
    }

    if (isModalOpen) {
      if (hasOverlay) {
        modalElement.showModal();
      } else {
        modalElement.show();
      }
    } else {
      modalElement.close();
    }
  }, [isModalOpen, hasOverlay]);

  // Close on backdrop click. The listener is attached to the dialog element directly
  // (rather than a JSX onClick) so the native <dialog> stays keyboard-accessible via Escape
  // without tripping the noninteractive-element interaction rules.
  useEffect(() => {
    const modalElement = dialogRef.current;
    if (!modalElement) {
      return;
    }

    const handleBackdropClick = (e: MouseEvent) => {
      const targetElement = e.target as HTMLDialogElement;
      if (targetElement?.nodeName !== 'DIALOG' && !hasOverlay && !onOverlayClick) {
        return;
      }

      const rect = targetElement.getBoundingClientRect();

      const clickedInDialog =
        rect.top <= e.clientY &&
        e.clientY <= rect.top + rect.height &&
        rect.left <= e.clientX &&
        e.clientX <= rect.left + rect.width;

      if (!clickedInDialog && onOverlayClick) {
        onOverlayClick();
      }
    };

    modalElement.addEventListener('click', handleBackdropClick);
    return () => {
      modalElement.removeEventListener('click', handleBackdropClick);
    };
  }, [hasOverlay, onOverlayClick]);

  const firstButtonClickHandler = () => {
    if (onFirstButtonClick) {
      onFirstButtonClick();
    }
    setIsModalOpen(false);
  };
  const secondButtonClickHandler = () => {
    if (onSecondButtonClick) {
      onSecondButtonClick();
    }
    setIsModalOpen(false);
  };

  return (
    <dialog className="m-auto w-[300px] p-0 h-fit border-none rounded-[8px] backdrop:bg-gray-300/75" ref={dialogRef}>
      <div className="flex justify-center" data-testid={testId}>
        <div
          className={cn('flex flex-col justify-center align-middle text-center max-w-[268px] leading-base text-base', {
            // grow so the content fills the dialog rather than shrink-wrapping the title.
            'm-16 grow': hasOverlay,
          })}
        >
          <div className={cn(hasOverlay ? 'pb-20' : 'pb-5')}>
            <div>
              {title && (
                <span data-testid={`${testId}-title`} className="text-base font-medium pt-0 px-4">
                  {title}
                </span>
              )}
              {price && (
                <span
                  className="bg-gray-300 rounded-[10px] leading-sm py-3 px-[7px] text-sm"
                  data-testid={MODAL_TEST_IDS.PRICE}
                >
                  {price}
                </span>
              )}
            </div>
            {description && (
              <div data-testid={`${testId}-description`} className="text-sm leading-base pt-8">
                {description}
              </div>
            )}
          </div>
          <div className={cn('flex-col gap-10', isButtonContentSize ? 'block' : 'flex')}>
            <Button
              onClick={firstButtonClickHandler}
              type={firstButtonType}
              isRectangular={isRectangular}
              testId={MODAL_TEST_IDS.PRIMARY_BTN}
              isFullSize={isFullSize}
            >
              {firstButtonText}
            </Button>
            {secondButtonText && (
              <Button
                onClick={secondButtonClickHandler}
                type={secondButtonType}
                isRectangular={isRectangular}
                testId={MODAL_TEST_IDS.SECONDARY_BTN}
              >
                {secondButtonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
}
